import { useEffect, useState } from "react";
import api, { apiError, localApi } from "@/lib/api";
import { applyBrandColor } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash, CircleNotch, Buildings, Timer, Percent, LockKey, Upload, ImageSquare, X } from "@phosphor-icons/react";

const inputCls = "rounded-sm border-input bg-background text-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0";

function NumInput({ value, onChange, ...props }) {
  return <Input type="number" step="any" className={inputCls} value={value} onChange={(e) => onChange(e.target.value)} {...props} />;
}

function EmpresaTab() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoSaving, setLogoSaving] = useState(false);

  useEffect(() => {
    localApi
      .get("/tenant-logo", { responseType: "blob" })
      .then((r) => setLogoUrl(URL.createObjectURL(r.data)))
      .catch(() => {});
  }, []);

  const [brand, setBrand] = useState("");
  const [brandSaving, setBrandSaving] = useState(false);

  useEffect(() => {
    localApi
      .get("/tenant/theme")
      .then((r) => setBrand(r.data?.primary || ""))
      .catch(() => {});
  }, []);

  const saveBrand = async () => {
    setBrandSaving(true);
    try {
      await localApi.put("/tenant/theme", { primary: brand });
      applyBrandColor(brand || null);
      if (brand) localStorage.setItem("caus_brand", JSON.stringify({ primary: brand }));
      else localStorage.removeItem("caus_brand");
      toast.success("Color de marca aplicado");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBrandSaving(false);
    }
  };

  const uploadLogo = async () => {
    if (!logoFile) return;
    setLogoSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", logoFile);
      await localApi.post("/tenant-logo", fd);
      toast.success("Logo actualizado");
      setLogoUrl(URL.createObjectURL(logoFile));
      setLogoFile(null);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLogoSaving(false);
    }
  };

  useEffect(() => {
    const load = (attempt = 0) => {
      api.get("/company").then((r) => setForm({
        razon_social: r.data.razon_social || "", nombre_comercial: r.data.nombre_comercial || "",
        rtn: r.data.rtn || "", direccion: r.data.direccion || "", correo: r.data.correo || "", telefono: r.data.telefono || "",
      })).catch(() => {
        if (attempt < 2) setTimeout(() => load(attempt + 1), 1500);
        else toast.error("No se pudieron cargar los datos de la empresa");
      });
    };
    load();
  }, []);

  if (!form) return <Loading />;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/company", form);
      toast.success("Datos de empresa actualizados");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div className="border border-border bg-card rounded-sm p-5" data-testid="logo-card">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Logo de la empresa</p>
        <div className="flex flex-wrap items-center gap-4">
          <div className="h-16 w-16 border border-border rounded-sm bg-background flex items-center justify-center overflow-hidden">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="h-full w-full object-contain" data-testid="logo-preview" />
            ) : (
              <ImageSquare size={22} className="text-muted-foreground" />
            )}
          </div>
          <div className="flex items-center gap-2">
            <input type="file" accept="image/*" className="hidden" id="logo-file" data-testid="logo-upload-input"
              onChange={(e) => setLogoFile(e.target.files?.[0] || null)} />
            <Button type="button" variant="outline" size="sm" className="rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent hover:text-accent-foreground"
              onClick={() => document.getElementById("logo-file").click()} data-testid="logo-choose-button">
              <Upload className="mr-2" size={14} /> {logoFile ? logoFile.name : "Elegir imagen"}
            </Button>
            <Button type="button" size="sm" disabled={!logoFile || logoSaving} onClick={uploadLogo}
              className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="logo-save-button">
              {logoSaving && <CircleNotch className="animate-spin mr-2" size={14} />} Guardar logo
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-3">Se usa en la planilla PDF de presentación y en los documentos descargables. Máx. 3 MB.</p>
      </div>
      <div className="border border-border bg-card rounded-sm p-5" data-testid="brand-card">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Color de marca</p>
        <div className="flex flex-wrap items-center gap-2">
          {["", "#0047FF", "#059669", "#D97706", "#DC2626", "#7C3AED"].map((c) => (
            <button key={c || "default"} type="button" onClick={() => setBrand(c)} title={c || "Predeterminado"}
              data-testid={`brand-swatch-${c ? c.slice(1) : "default"}`}
              className={`h-9 w-9 rounded-sm border ${brand === c ? "ring-2 ring-ring border-foreground" : "border-border"}`}
              style={{ backgroundColor: c || "transparent" }}>
              {!c && <X size={14} className="mx-auto text-muted-foreground" />}
            </button>
          ))}
          <input type="color" value={brand || "#0047FF"} onChange={(e) => setBrand(e.target.value)}
            className="h-9 w-14 cursor-pointer bg-transparent border border-border rounded-sm" data-testid="brand-color-custom" />
          <Button type="button" size="sm" onClick={saveBrand} disabled={brandSaving}
            className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="brand-save-button">
            {brandSaving && <CircleNotch className="animate-spin mr-2" size={14} />} Aplicar color
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-3">Se aplica a los botones y acentos del portal de tu empresa.</p>
      </div>
    <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl" data-testid="company-form">
      {[
        ["razon_social", "Razón social *", true], ["nombre_comercial", "Nombre comercial"], ["rtn", "RTN"],
        ["correo", "Correo"], ["telefono", "Teléfono"], ["direccion", "Dirección"],
      ].map(([k, label, req]) => (
        <div key={k} className="space-y-1.5">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</Label>
          <Input className={inputCls} required={!!req} value={form[k]} onChange={set(k)} data-testid={`company-${k}`} />
        </div>
      ))}
      <div className="sm:col-span-2">
        <Button type="submit" disabled={saving} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="company-save-button">
          {saving && <CircleNotch className="animate-spin mr-2" size={15} />} Guardar empresa
        </Button>
      </div>
    </form>
    </div>
  );
}

function HorasExtraTab() {
  const [rates, setRates] = useState(null);
  const [tiers, setTiers] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/settings/overtime"), api.get("/overtime-rules")])
      .then(([r, t]) => {
        setRates(Array.isArray(r.data) ? r.data : []);
        setTiers(t.data?.tiers || []);
      })
      .catch((e) => toast.error(apiError(e)));
  }, []);

  if (!rates || !tiers) return <Loading />;

  const saveRates = async () => {
    setSaving(true);
    try {
      await api.put("/settings/overtime", rates.map((r) => ({ ...r, pct: Number(r.pct) || 0 })));
      toast.success("Tipos de hora extra guardados");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const saveTiers = async () => {
    setSaving(true);
    try {
      await api.put("/overtime-rules", { tiers: tiers.map((t) => ({ ...t, max_hours: Number(t.max_hours) || 0, multiplier: Number(t.multiplier) || 0 })) });
      toast.success("Reglas de horas extra guardadas");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="border border-border bg-card rounded-sm p-5" data-testid="overtime-rates-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-bold text-foreground">Tipos de hora extra</h3>
          <Button variant="outline" size="sm" className="rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent hover:text-accent-foreground" data-testid="add-rate-button"
            onClick={() => setRates([...rates, { name: "", pct: 0, description: "" }])}>
            <Plus className="mr-1.5" size={14} /> Agregar
          </Button>
        </div>
        <div className="space-y-3">
          {rates.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_90px_32px] gap-2 items-center">
              <Input className={inputCls} placeholder="Nombre" value={r.name} data-testid={`rate-name-${i}`}
                onChange={(e) => setRates(rates.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
              <NumInput value={r.pct} data-testid={`rate-pct-${i}`} onChange={(v) => setRates(rates.map((x, j) => (j === i ? { ...x, pct: v } : x)))} />
              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-400" data-testid={`rate-del-${i}`}
                onClick={() => setRates(rates.filter((_, j) => j !== i))}>
                <Trash size={14} />
              </Button>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-3">El porcentaje se expresa como factor (0.25 = 25%).</p>
        <Button onClick={saveRates} disabled={saving} className="mt-4 rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="rates-save-button">
          {saving && <CircleNotch className="animate-spin mr-2" size={15} />} Guardar tipos
        </Button>
      </div>

      <div className="border border-border bg-card rounded-sm p-5" data-testid="overtime-tiers-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-bold text-foreground">Reglas por nivel</h3>
          <Button variant="outline" size="sm" className="rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent hover:text-accent-foreground" data-testid="add-tier-button"
            onClick={() => setTiers([...tiers, { label: "", max_hours: 0, multiplier: 1 }])}>
            <Plus className="mr-1.5" size={14} /> Agregar
          </Button>
        </div>
        <div className="space-y-3">
          {tiers.map((t, i) => (
            <div key={i} className="grid grid-cols-[1fr_90px_90px_32px] gap-2 items-center">
              <Input className={inputCls} placeholder="Etiqueta" value={t.label} data-testid={`tier-label-${i}`}
                onChange={(e) => setTiers(tiers.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
              <NumInput value={t.max_hours} data-testid={`tier-hours-${i}`} onChange={(v) => setTiers(tiers.map((x, j) => (j === i ? { ...x, max_hours: v } : x)))} />
              <NumInput value={t.multiplier} data-testid={`tier-mult-${i}`} onChange={(v) => setTiers(tiers.map((x, j) => (j === i ? { ...x, multiplier: v } : x)))} />
              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-400" data-testid={`tier-del-${i}`}
                onClick={() => setTiers(tiers.filter((_, j) => j !== i))}>
                <Trash size={14} />
              </Button>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-3">Columnas: etiqueta, horas máximas (0 = sin límite), multiplicador.</p>
        <Button onClick={saveTiers} disabled={saving} className="mt-4 rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="tiers-save-button">
          {saving && <CircleNotch className="animate-spin mr-2" size={15} />} Guardar reglas
        </Button>
      </div>
    </div>
  );
}

function DeduccionesTab() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/deductions").then((r) => setForm(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  if (!form) return <Loading />;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings/deductions", {
        ...form,
        ihss_rate: Number(form.ihss_rate) || 0,
        ihss_ceiling: Number(form.ihss_ceiling) || 0,
        rap_rate: Number(form.rap_rate) || 0,
        rap_ceiling: Number(form.rap_ceiling) || 0,
        medical_monthly_deduction: Number(form.medical_monthly_deduction) || 0,
        isr_brackets: form.isr_brackets.map((b) => ({ up_to: Number(b.up_to) || 0, rate: Number(b.rate) || 0 })),
      });
      toast.success("Deducciones actualizadas");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-3xl space-y-6" data-testid="deductions-form">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {[
          ["ihss_rate", "Tasa IHSS"], ["ihss_ceiling", "Techo IHSS (L)"], ["rap_rate", "Tasa RAP"],
          ["rap_ceiling", "Techo RAP (L)"], ["medical_monthly_deduction", "Deducción médica mensual (L)"],
        ].map(([k, label]) => (
          <div key={k} className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</Label>
            <NumInput value={form[k]} data-testid={`deduction-${k}`} onChange={(v) => setForm({ ...form, [k]: v })} />
          </div>
        ))}
      </div>
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg font-bold text-foreground">Tramos de ISR</h3>
          <Button type="button" variant="outline" size="sm" className="rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent hover:text-accent-foreground" data-testid="add-bracket-button"
            onClick={() => setForm({ ...form, isr_brackets: [...form.isr_brackets, { up_to: 0, rate: 0 }] })}>
            <Plus className="mr-1.5" size={14} /> Tramo
          </Button>
        </div>
        <div className="space-y-2">
          {form.isr_brackets.map((b, i) => (
            <div key={i} className="grid grid-cols-[1fr_120px_32px] gap-2 items-center">
              <NumInput value={b.up_to} data-testid={`bracket-upto-${i}`} onChange={(v) => setForm({ ...form, isr_brackets: form.isr_brackets.map((x, j) => (j === i ? { ...x, up_to: v } : x)) })} />
              <NumInput value={b.rate} data-testid={`bracket-rate-${i}`} onChange={(v) => setForm({ ...form, isr_brackets: form.isr_brackets.map((x, j) => (j === i ? { ...x, rate: v } : x)) })} />
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-400" data-testid={`bracket-del-${i}`}
                onClick={() => setForm({ ...form, isr_brackets: form.isr_brackets.filter((_, j) => j !== i) })}>
                <Trash size={14} />
              </Button>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-2">Columnas: hasta (L), tasa (0.15 = 15%).</p>
      </div>
      <Button type="submit" disabled={saving} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="deductions-save-button">
        {saving && <CircleNotch className="animate-spin mr-2" size={15} />} Guardar deducciones
      </Button>
    </form>
  );
}

function SeguridadTab() {
  const { user, logout } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) {
      toast.error("Las contraseñas nuevas no coinciden");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Contraseña actualizada. Vuelve a iniciar sesión.");
      logout();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-md space-y-6" data-testid="security-tab">
      <div className="border border-border bg-card rounded-sm p-5">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Sesión actual</p>
        <p className="text-foreground font-medium mt-2">{user?.full_name}</p>
        <p className="text-sm text-muted-foreground">{user?.username} · rol {user?.role}</p>
      </div>
      <form onSubmit={save} className="space-y-4" data-testid="change-password-form">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Contraseña actual</Label>
          <Input type="password" required className={inputCls} data-testid="current-password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Nueva contraseña</Label>
          <Input type="password" required className={inputCls} data-testid="new-password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Confirmar nueva contraseña</Label>
          <Input type="password" required className={inputCls} data-testid="confirm-password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} />
        </div>
        <Button type="submit" disabled={saving} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="change-password-button">
          {saving && <CircleNotch className="animate-spin mr-2" size={15} />} Cambiar contraseña
        </Button>
      </form>
    </div>
  );
}

export default function Configuracion() {
  return (
    <div data-testid="configuracion-page">
      <PageHeader title="Configuración" description="Datos de la empresa, deducciones de ley y seguridad" testid="configuracion-header" />
      <Tabs defaultValue="empresa">
        <TabsList className="rounded-sm bg-muted border border-border flex-wrap h-auto" data-testid="config-tabs">
          <TabsTrigger value="empresa" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-empresa">
            <Buildings className="mr-1.5" size={14} /> Empresa
          </TabsTrigger>
          <TabsTrigger value="horas" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-horas">
            <Timer className="mr-1.5" size={14} /> Horas extra
          </TabsTrigger>
          <TabsTrigger value="deducciones" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-deducciones">
            <Percent className="mr-1.5" size={14} /> Deducciones
          </TabsTrigger>
          <TabsTrigger value="seguridad" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-seguridad">
            <LockKey className="mr-1.5" size={14} /> Seguridad
          </TabsTrigger>
        </TabsList>
        <TabsContent value="empresa" className="mt-6"><EmpresaTab /></TabsContent>
        <TabsContent value="horas" className="mt-6"><HorasExtraTab /></TabsContent>
        <TabsContent value="deducciones" className="mt-6"><DeduccionesTab /></TabsContent>
        <TabsContent value="seguridad" className="mt-6"><SeguridadTab /></TabsContent>
      </Tabs>
    </div>
  );
}
